import React, { useState, useEffect } from 'react';
import { Search, FileDown, Plus, X, Eye, FileText, GraduationCap, Calendar, Mail, Phone, BookOpen, Clock, ArrowRight, CheckCircle2, AlertCircle, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import formularioDoc from './Formulario de inscripcion.docx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

const KardexInscripciones = () => {
    const currentYear = new Date().getFullYear();
    const [gestion, setGestion] = useState(currentYear);
    const [estudiantes, setEstudiantes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    // Modal Details States
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedEstDetails, setSelectedEstDetails] = useState(null);

    // Modal Designar Materias States
    const [showDesignarModal, setShowDesignarModal] = useState(false);
    const [designarEstudiante, setDesignarEstudiante] = useState(null);
    const [ofertaAsignaturas, setOfertaAsignaturas] = useState([]);
    const [selectedAsignaciones, setSelectedAsignaciones] = useState([]);
    const [loadingOferta, setLoadingOferta] = useState(false);
    const [savingDesignacion, setSavingDesignacion] = useState(false);
    const [searchOferta, setSearchOferta] = useState('');
    const [toast, setToast] = useState(null);

    const showToast = (type, title, message) => {
        setToast({ type, title, message });
        setTimeout(() => {
            setToast(null);
        }, 4500);
    };

    useEffect(() => {
        loadInscripciones();
    }, [gestion]);

    // Search with debounce
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            loadInscripciones();
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [search]);

    const loadInscripciones = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({ gestion });
            if (search) params.append('search', search);

            const response = await fetch(`${API_URL}/kardex/inscripciones-asignaturas?${params}`);
            const data = await response.json();
            setEstudiantes(data.estudiantes || []);
        } catch (error) {
            console.error('Error loading inscripciones:', error);
            setEstudiantes([]);
        } finally {
            setLoading(false);
        }
    };

    const downloadExcel = () => {
        const headers = [
            "Codigo", "Nombres", "Apellidos", "CI", "Fecha Nacimiento",
            "Admision", "Estado", "Celular", "Correo Electronico",
            "Carrera", "Mencion", "Matriculado"
        ];
        const data = estudiantes.map(e => [
            e.codigo_estudiante || 'N/A', e.nombres, e.apellidos, e.cedula_identidad,
            e.fecha_nacimiento ? new Date(e.fecha_nacimiento).toLocaleDateString() : 'N/A',
            e.etapa_academica?.replace('_', ' ') || 'N/A',
            e.etapa_academica === 'matriculado' ? 'ACTIVO' : 'EN PROCESO',
            e.celular || 'N/A', e.correo_electronico || 'N/A',
            e.carrera_nombre || 'N/A', e.mencion_nombre || 'Sin Mención',
            e.etapa_academica === 'matriculado' ? 'SI' : 'NO'
        ]);

        const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Inscripciones");
        XLSX.writeFile(wb, `Reporte_Inscripciones_${gestion}.xlsx`);
    };

    const openDetails = (estudiante) => {
        setSelectedEstDetails(estudiante);
        setShowDetailModal(true);
    };

    const openDesignarModal = async (estudiante) => {
        setDesignarEstudiante(estudiante);
        setShowDesignarModal(true);
        setLoadingOferta(true);
        setSearchOferta('');
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
            const res = await fetch(`${API_URL}/kardex/estudiantes/${estudiante.id_estudiante}/oferta-asignaturas?gestion=${gestion}`, { headers });
            const data = await res.json();
            const asignaturas = data.asignaturas || [];
            setOfertaAsignaturas(asignaturas);
            const yaSeleccionadas = asignaturas.filter(a => a.seleccionado).map(a => a.id_asignacion);
            setSelectedAsignaciones(yaSeleccionadas);
        } catch (error) {
            console.error('Error al cargar oferta de asignaturas:', error);
            setOfertaAsignaturas([]);
            setSelectedAsignaciones([]);
        } finally {
            setLoadingOferta(false);
        }
    };

    const toggleAsignacion = (id_asignacion) => {
        setSelectedAsignaciones(prev => {
            if (prev.includes(id_asignacion)) {
                return prev.filter(id => id !== id_asignacion);
            } else {
                return [...prev, id_asignacion];
            }
        });
    };

    const handleSelectAllOferta = (filteredList) => {
        const ids = filteredList.map(a => a.id_asignacion);
        setSelectedAsignaciones(prev => Array.from(new Set([...prev, ...ids])));
    };

    const handleClearAllOferta = () => {
        setSelectedAsignaciones([]);
    };

    const handleSaveDesignacion = async () => {
        if (!designarEstudiante) return;
        setSavingDesignacion(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/kardex/estudiantes/${designarEstudiante.id_estudiante}/designar-materias`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    gestion,
                    asignaciones: selectedAsignaciones
                })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Error al guardar designación');

            setShowDesignarModal(false);
            loadInscripciones();
            showToast('success', 'Asignación Guardada', data.message || 'Materias registradas exitosamente');
        } catch (error) {
            console.error('Error al guardar designación:', error);
            showToast('error', 'Error en Asignación', error.message || 'Error al procesar la designación de materias');
        } finally {
            setSavingDesignacion(false);
        }
    };

    const downloadFormulario = async (estudiante) => {
        try {
            // 1. Obtener el archivo como binario
            const response = await fetch(formularioDoc);
            const content = await response.arrayBuffer();

            // 2. Cargar en PizZip
            const zip = new PizZip(content);

            // 3. Configurar Docxtemplater
            const doc = new Docxtemplater(zip, {
                paragraphLoop: true,
                linebreaks: true,
            });

            // 4. Preparar la data formateada
            const getAdmisionLabel = (etapa) => {
                const labels = {
                    'admision_especial': 'Admisión Especial',
                    'especial': 'Admisión Especial',
                    'preadmision_prueba': 'Prueba de Admisión',
                    'preadmision_curso': 'Curso Preadmisión',
                    'admision_aprobado': 'Admisión Aprobada',
                    'postulante': 'Postulante',
                    'matriculado': 'Matriculado',
                    'licenciatura': 'Licenciatura'
                };
                return labels[etapa] || etapa?.replace(/_/g, ' ') || '---';
            };

            const dataToRender = {
                ...estudiante,
                nombre_completo: `${estudiante.nombres} ${estudiante.apellidos}`.toUpperCase(),
                ci: estudiante.cedula_identidad,
                admision_modalidad: getAdmisionLabel(estudiante.etapa_academica),
                admision_gestion: gestion,
                gestion,
                carrera: estudiante.carrera_nombre || '---',
                mencion: estudiante.mencion_nombre || '---',
                fecha_nacimiento: estudiante.fecha_nacimiento
                    ? new Date(estudiante.fecha_nacimiento).toLocaleDateString()
                    : '---',
                // asignaturas: estudiante.asignaturas || []
                asignaturas: (estudiante.asignaturas || []).map((asig, index) => ({
                    numero: index + 1,
                    asignatura_codigo: asig.asignatura_codigo || '---',
                    asignatura_nombre: asig.asignatura_nombre || '---',
                    paralelo: asig.paralelo || '---',
                    nivel_texto: asig.nivel === 1 ? '1er Año' :
                        asig.nivel === 2 ? '2do Año' :
                            asig.nivel === 3 ? '3er Año' :
                                asig.nivel === 4 ? '4to Año' :
                                    asig.nivel === 5 ? '5to Año' : 'N/A'
                }))
            };
            // 5. Inyectar los datos en el template
            doc.render(dataToRender);

            // 6. Generar el blob final
            const out = doc.getZip().generate({
                type: 'blob',
                mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            });

            // 7. Descargar el archivo
            const link = document.createElement('a');
            const url = URL.createObjectURL(out);
            link.href = url;
            link.setAttribute('download', `Formulario_Inscripcion_${estudiante.apellidos}_${estudiante.nombres}.docx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Error generating dynamic Word:', error);
            // Fallback simple por si falla (descarga el original sin cambios)
            const link = document.createElement('a');
            link.href = formularioDoc;
            link.setAttribute('download', `Formulario_Inscripcion_${estudiante.apellidos}_${estudiante.nombres}.docx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <div className="p-6 relative">

            {/* Notification Toast - Línea Gráfica EBID */}
            {toast && (
                <div className="fixed top-6 right-6 z-[9999] flex items-center gap-3.5 px-5 py-3.5 bg-white rounded-2xl shadow-2xl border border-slate-100 max-w-md animate-in slide-in-from-top-4 fade-in duration-300">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        toast.type === 'success' 
                            ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' 
                            : 'bg-rose-50 text-rose-600 border border-rose-200'
                    }`}>
                        {toast.type === 'success' ? <CheckCircle2 size={20} strokeWidth={2.5} /> : <AlertCircle size={20} strokeWidth={2.5} />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h4 className="text-xs font-bold text-slate-900 leading-tight">
                            {toast.title}
                        </h4>
                        <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                            {toast.message}
                        </p>
                    </div>
                    <button
                        onClick={() => setToast(null)}
                        className="w-6 h-6 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 flex items-center justify-center transition"
                    >
                        <X size={14} />
                    </button>
                </div>
            )}

            {/* Action Bar Header - Aligned to Left */}
            <div className="mb-6">
                <div className="mb-6">
                    <h1 className="text-xl font-bold text-gray-800">Kardex &gt; Registro de Asignaturas</h1>
                </div>
                <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex flex-wrap gap-4 items-end">

                        <div className="flex-grow flex items-end gap-2">
                            <div className="relative flex-grow">
                                {/* <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label> */}
                                <div className="relative">
                                    <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                    <input
                                        type="text"
                                        placeholder="Buscar estudiante o CI..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                    />
                                </div>
                            </div>
                            <button
                                onClick={downloadExcel}
                                className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm"
                            >
                                <Download size={18} />
                            </button>


                        </div>
                    </div>
                </div>
                {/* <div className="flex flex-wrap items-center gap-4">
                    
                    <div className="flex items-center bg-white p-1 rounded-xl border border-slate-200 shadow-sm hover:border-slate-300 transition-all group">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-hover:text-blue-500 transition-colors" size={15} />
                            <input
                                type="text"
                                placeholder="Buscar estudiante o CI..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="bg-transparent border-none pl-9 pr-4 py-1.5 text-xs font-bold w-56 focus:ring-0 outline-none placeholder:text-slate-300 text-slate-600"
                            />
                        </div>
                    </div>

                    <button
                        onClick={downloadExcel}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] font-black px-5 py-3 rounded-xl transition-all flex items-center gap-2 group shadow-md shadow-emerald-50 active:scale-95 border-b-2 border-emerald-700"
                    >
                        <FileDown size={14} className="group-hover:translate-y-0.5 transition-transform" />
                    </button>

            
                </div> */}

            </div>

            {/* Grouped High-Density Table - Balanced & Natural */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-3 text-center font-semibold border">Código</th>
                                <th className="px-3 py-3 text-center font-semibold border">Nombres</th>
                                <th className="px-3 py-3 text-center font-semibold border">Apellidos</th>
                                <th className="px-3 py-3 text-center font-semibold border">C.I.</th>
                                <th className="px-3 py-3 text-center font-semibold border">Nac.</th>
                                <th className="px-3 py-3 text-center font-semibold border">Admisión</th>
                                <th className="px-3 py-3 text-center font-semibold border">Celular</th>
                                <th className="px-3 py-3 text-center font-semibold border">Correo</th>
                                <th className="px-3 py-3 text-center font-semibold border">Carrera</th>
                                <th className="px-3 py-3 text-center font-semibold border">Mención</th>
                                <th className="px-3 py-3 text-center font-semibold border">Mat.</th>
                                <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                                <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array(5).fill(0).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan={13} className="px-6 py-6"><div className="h-6 bg-slate-50 rounded-lg w-full"></div></td>
                                    </tr>
                                ))
                            ) : estudiantes.length === 0 ? (
                                <tr>
                                    <td colSpan={13} className="px-6 py-20 text-center text-slate-400 font-medium italic">No se encontraron inscripciones activas para la gestión {gestion}</td>
                                </tr>
                            ) : (
                                estudiantes.map((est, index) => (
                                    <tr key={est.id_estudiante || `pre-${est.id_inscripcion_pre}-${index}`} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 text-center border">
                                            <div >{est.codigo_estudiante || 'N/A'}</div>
                                        </td>
                                        <td className="px-3 py-2 border">
                                            <div className="uppercase">{est.nombres}</div>
                                        </td>
                                        <td className="px-3 py-2 border">
                                            <div className="uppercase">{est.apellidos}</div>
                                        </td>
                                        <td className="px-3 py-2 border">
                                            <div>{est.cedula_identidad}</div>
                                        </td>
                                        <td className="px-3 py-2 border">
                                            <div>
                                                {est.fecha_nacimiento ? new Date(est.fecha_nacimiento).toLocaleDateString() : '---'}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 border">
                                            {(() => {
                                                const etapa = est.etapa_academica;
                                                let colorClass = 'bg-emerald-50 text-emerald-600 border-emerald-100';
                                                let label = etapa?.replace(/_/g, ' ') || '---';
                                                if (etapa === 'admision_especial' || etapa === 'especial') {
                                                    colorClass = 'bg-amber-50 text-amber-600 border-amber-100';
                                                    label = 'Admisión Especial';
                                                } else if (etapa === 'preadmision_prueba') {
                                                    colorClass = 'bg-blue-50 text-blue-600 border-blue-100';
                                                    label = 'Prueba de Admisión';
                                                } else if (etapa === 'preadmision_curso') {
                                                    colorClass = 'bg-indigo-50 text-indigo-600 border-indigo-100';
                                                    label = 'Curso Preadmisión';
                                                } else if (etapa === 'admision_aprobado') {
                                                    colorClass = 'bg-teal-50 text-teal-600 border-teal-100';
                                                    label = 'Admisión Aprobada';
                                                } else if (etapa === 'postulante') {
                                                    colorClass = 'bg-slate-50 text-slate-500 border-slate-200';
                                                    label = 'Postulante';
                                                } else if (etapa === 'matriculado') {
                                                    colorClass = 'bg-emerald-50 text-emerald-600 border-emerald-100';
                                                    label = 'Matriculado';
                                                } else if (etapa === 'licenciatura') {
                                                    colorClass = 'bg-purple-50 text-purple-600 border-purple-100';
                                                    label = 'Licenciatura';
                                                }
                                                return (
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
                                                        {label}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td className="px-3 py-2 border">
                                            {est.celular || 'S/N'}
                                        </td>
                                        <td className="px-3 py-2 border">
                                            <div className="text-[10px] text-slate-500 font-medium truncate max-w-[100px]" title={est.correo_electronico}>
                                                {est.correo_electronico || 'S/E'}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 border">
                                            <div className={`text-[10px] font-bold leading-tight uppercase ${est.carrera_nombre ? 'text-slate-600' : 'text-amber-500 italic'}`}>
                                                {est.carrera_nombre || 'PENDIENTE'}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 border">
                                            <div className="text-[10px] text-slate-400 font-bold italic truncate max-w-[100px] uppercase">
                                                {est.mencion_nombre || 'S/M'}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 border">
                                            {est.etapa_academica === 'matriculado' ? (
                                                <div className="flex items-center justify-center text-emerald-500" title="MATRICULADO">
                                                    <CheckCircle2 size={16} strokeWidth={3} />
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-center text-slate-300" title="PENDIENTE">
                                                    <AlertCircle size={14} />
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 border">
                                            <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full uppercase ${est.etapa_academica === 'matriculado'
                                                ? 'bg-emerald-100 text-emerald-700'
                                                : 'bg-blue-100 text-blue-700'
                                                }`}>
                                                {est.etapa_academica === 'matriculado' ? 'ACTIVO' : 'EN PROCESO'}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 border">
                                            <div className="flex items-center justify-center gap-1.5">
                                                {est.asignaturas && est.asignaturas.length > 0 ? (
                                                    <button
                                                        onClick={() => openDesignarModal(est)}
                                                        className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-all shadow-sm border border-emerald-200 flex items-center gap-1 font-semibold text-[11px] whitespace-nowrap"
                                                        title="Modificar Asignaturas Inscritas"
                                                    >
                                                        <BookOpen size={13} />
                                                        <span>{est.asignaturas.length} Asig.</span>
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => openDesignarModal(est)}
                                                        className="px-2.5 py-1 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-all shadow-sm flex items-center gap-1 font-bold text-[11px] whitespace-nowrap"
                                                        title="Registrar / Inscribir Asignaturas para este Estudiante"
                                                    >
                                                        <Plus size={13} strokeWidth={2.5} />
                                                        <span>Inscribir</span>
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => openDetails(est)}
                                                    className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all shadow-sm border border-blue-100"
                                                    title="Ver Detalle de Asignaturas"
                                                >
                                                    <Eye size={14} />
                                                </button>
                                                <button
                                                    onClick={() => downloadFormulario(est)}
                                                    className="w-7 h-7 rounded-lg bg-slate-50 text-slate-500 flex items-center justify-center hover:bg-slate-800 hover:text-white transition-all shadow-sm border border-slate-100"
                                                    title="Descargar Formulario de Inscripción"
                                                >
                                                    <FileText size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Details Modal (Eye Button) */}
            {showDetailModal && selectedEstDetails && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50 shadow-2xl" onClick={() => setShowDetailModal(false)}></div>
                    <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl relative overflow-hidden flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-300 border border-slate-200">
                        {/* Header Modal */}
                        <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center flex-shrink-0">
                            <div>
                                {/* <div className="flex items-center gap-3"> */}
                                    {/* <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center backdrop-blur-md">
                                        <GraduationCap size={18} />
                                    </div> */}
                                    <h3 className="text-xl font-bold text-blue-900">Detalle de Inscripción</h3>
                                {/* </div> */}
                                <p className="text-sm text-gray-600">
                                    {selectedEstDetails.apellidos}, {selectedEstDetails.nombres}
                                </p>
                            </div>
                            <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
                        </div>

                        {/* Modal Content */}
                        <div className="p-6 overflow-y-auto flex-1 max-h-[65vh] scrollbar-thin">
                            {selectedEstDetails.asignaturas && selectedEstDetails.asignaturas.length > 0 ? (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between px-2">
                                        <p className="text-lg font-semibold text-blue-800 mb-4">
                                            {/* <BookOpen size={14} className="text-blue-500" /> */}
                                            Asignaturas Registradas ({selectedEstDetails.asignaturas.length})
                                        </p>
                                        <span className="font-semibold text-blue-800 mb-4">GESTIÓN {gestion}</span>
                                    </div>

                                    <div className="bg-white rounded-lg shadow overflow-hidden">
                                        <table className="w-full text-sm">
                                            <thead className="bg-gray-100 border-b">
                                                <tr>
                                                    <th className="px-3 py-3 text-center font-semibold border">Código</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Asignatura</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Nivel</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Paralelo</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Horario</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {selectedEstDetails.asignaturas.map(asig => (
                                                    <tr key={asig.id_asignacion} className="hover:bg-gray-50">
                                                        <td className="px-3 py-2 text-center border">{asig.asignatura_codigo || '---'}</td>
                                                        <td className="px-3 py-2 border">
                                                            <div >{asig.asignatura_nombre}</div>
                                                        </td>
                                                        <td className="px-3 py-2 border text-center">
                                                            <div className="">
                                                                {asig.nivel === 1 ? '1er Año' : asig.nivel === 2 ? '2do Año' : asig.nivel === 3 ? '3er Año' : asig.nivel === 4 ? '4to Año' : asig.nivel === 5 ? '5to Año' : 'N/A'}
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-2 border text-center">
                                                            <span className="">
                                                                P-{asig.paralelo}
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2 border text-center">
                                                            <div className="flex flex-col items-center">
                                                                <div className="flex items-center gap-1 text-[9px] font-bold ">
                                                                    {asig.hora_inicio?.slice(0, 5)} - {asig.hora_fin?.slice(0, 5)}
                                                                </div>
                                                                <div className="text-[8px] font-bold text-grey-200 uppercase">{asig.sala || 'S/A'}</div>
                                                            </div>
                                                        </td>
                                                    </tr>

                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                <div className="py-16 flex flex-col items-center justify-center text-slate-400 gap-3 border-2 border-dashed border-slate-100 rounded-2xl">
                                    <BookOpen size={32} className="opacity-20" />
                                    <p className="font-medium italic text-sm">No existen asignaturas inscritas para esta gestión.</p>
                                </div>
                            )}
                        </div>

                        {/* Footer Modal */}
                        <div className="p-4 border-t flex justify-end">
                            <button
                                onClick={() => setShowDetailModal(false)}
                                className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Designación de Materias */}
            {showDesignarModal && designarEstudiante && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden border border-slate-100">
                        {/* Header Modal */}
                        <div className="px-6 py-4 bg-gradient-to-r from-blue-900 to-indigo-900 text-white flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white/10 rounded-xl">
                                    <BookOpen size={20} className="text-white" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold">Designación de Materias (Gestión {gestion})</h2>
                                    <p className="text-xs text-blue-200">
                                        Asignación directa de asignaturas y paralelos para el estudiante
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowDesignarModal(false)}
                                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Banner Estudiante */}
                        <div className="bg-blue-50/70 border-b border-blue-100 px-6 py-3 flex flex-wrap items-center justify-between gap-3 text-xs">
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-blue-950 uppercase text-sm">
                                    {designarEstudiante.nombres} {designarEstudiante.apellidos}
                                </span>
                                <span className="bg-white text-blue-800 font-mono font-bold px-2 py-0.5 rounded border border-blue-200">
                                    {designarEstudiante.codigo_estudiante || 'S/C'}
                                </span>
                                <span className="text-slate-500 font-mono">
                                    CI: {designarEstudiante.cedula_identidad || 'S/N'}
                                </span>
                            </div>
                            <div className="flex items-center gap-3 text-slate-600">
                                <span><strong>Carrera:</strong> {designarEstudiante.carrera_nombre || 'Sin Carrera'}</span>
                                {designarEstudiante.mencion_nombre && (
                                    <span><strong>Mención:</strong> {designarEstudiante.mencion_nombre}</span>
                                )}
                                <span className={`px-2 py-0.5 rounded-full font-bold uppercase text-[10px] ${
                                    designarEstudiante.etapa_academica === 'matriculado'
                                        ? 'bg-emerald-100 text-emerald-700'
                                        : 'bg-blue-100 text-blue-700'
                                }`}>
                                    {designarEstudiante.etapa_academica === 'matriculado' ? 'ACTIVO' : 'EN PROCESO'}
                                </span>
                            </div>
                        </div>

                        {/* Controles de Búsqueda y Selección */}
                        <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex flex-wrap items-center justify-between gap-3">
                            <div className="relative flex-grow max-w-md">
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Filtrar por código, nombre de materia, paralelo o docente..."
                                    value={searchOferta}
                                    onChange={(e) => setSearchOferta(e.target.value)}
                                    className="w-full pl-9 pr-4 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        const nivel1Ids = ofertaAsignaturas
                                            .filter(a => parseInt(a.nivel) === 1 || a.nivel === '1')
                                            .map(a => a.id_asignacion);
                                        setSelectedAsignaciones(prev => Array.from(new Set([...prev, ...nivel1Ids])));
                                    }}
                                    className="px-2.5 py-1.5 text-xs font-semibold bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg hover:bg-emerald-100 transition shadow-sm flex items-center gap-1"
                                    title="Selecciona automáticamente todas las materias de 1er año"
                                >
                                    <span>+ Todo 1er Año</span>
                                </button>
                                {(() => {
                                    const filteredOferta = ofertaAsignaturas.filter(a => {
                                        if (!searchOferta.trim()) return true;
                                        const term = searchOferta.trim().toLowerCase();
                                        const str = `${a.asignatura_codigo} ${a.asignatura_nombre} ${a.paralelo} ${a.docente_nombre} nivel ${a.nivel}`.toLowerCase();
                                        return str.includes(term);
                                    });
                                    return (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => handleSelectAllOferta(filteredOferta)}
                                                className="px-2.5 py-1.5 text-xs font-semibold bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-100 transition shadow-sm"
                                            >
                                                Marcar visibles
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleClearAllOferta}
                                                className="px-2.5 py-1.5 text-xs font-semibold bg-white border border-slate-200 text-rose-600 rounded-lg hover:bg-rose-50 transition shadow-sm"
                                            >
                                                Desmarcar todas
                                            </button>
                                        </>
                                    );
                                })()}
                                <span className="text-xs font-bold px-3 py-1.5 bg-blue-100 text-blue-900 rounded-lg">
                                    {selectedAsignaciones.length} seleccionada(s)
                                </span>
                            </div>
                        </div>

                        {/* Cuerpo de la Oferta */}
                        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                            {loadingOferta ? (
                                <div className="py-20 flex flex-col items-center justify-center gap-3">
                                    <div className="w-10 h-10 border-4 border-blue-900 border-t-transparent rounded-full animate-spin"></div>
                                    <p className="text-sm text-slate-500 font-medium">Cargando oferta académica...</p>
                                </div>
                            ) : (() => {
                                const filteredOferta = ofertaAsignaturas.filter(a => {
                                    if (!searchOferta.trim()) return true;
                                    const term = searchOferta.trim().toLowerCase();
                                    const str = `${a.asignatura_codigo} ${a.asignatura_nombre} ${a.paralelo} ${a.docente_nombre} nivel ${a.nivel}`.toLowerCase();
                                    return str.includes(term);
                                });

                                if (filteredOferta.length === 0) {
                                    return (
                                        <div className="py-16 text-center text-slate-400 italic">
                                            {ofertaAsignaturas.length === 0
                                                ? `No hay oferta de asignaturas programada para esta carrera en la gestión ${gestion}.`
                                                : `No se encontraron asignaturas que coincidan con "${searchOferta}".`}
                                        </div>
                                    );
                                }

                                return (
                                    <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                                        <table className="w-full text-xs">
                                            <thead className="bg-slate-100 text-slate-700 font-bold border-b">
                                                <tr>
                                                    <th className="px-3 py-2 text-center w-12">Sel.</th>
                                                    <th className="px-3 py-2 text-center w-20">Código</th>
                                                    <th className="px-3 py-2 text-left">Asignatura</th>
                                                    <th className="px-3 py-2 text-center w-16">Nivel</th>
                                                    <th className="px-3 py-2 text-center w-16">Par.</th>
                                                    <th className="px-3 py-2 text-left">Docente</th>
                                                    <th className="px-3 py-2 text-center">Horario / Días</th>
                                                    <th className="px-3 py-2 text-center w-16">Sala</th>
                                                    <th className="px-3 py-2 text-center w-20">Cupo</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {filteredOferta.map(asig => {
                                                    const isChecked = selectedAsignaciones.includes(asig.id_asignacion);
                                                    const cupoLleno = asig.inscritos >= asig.capacidad;
                                                    return (
                                                        <tr
                                                            key={asig.id_asignacion}
                                                            onClick={() => toggleAsignacion(asig.id_asignacion)}
                                                            className={`cursor-pointer transition ${
                                                                isChecked ? 'bg-blue-50/80 hover:bg-blue-100/70 font-medium' : 'hover:bg-slate-50'
                                                            }`}
                                                        >
                                                            <td className="px-3 py-2 text-center">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    onChange={() => {}}
                                                                    className="w-4 h-4 rounded text-blue-900 focus:ring-blue-500 cursor-pointer"
                                                                />
                                                            </td>
                                                            <td className="px-3 py-2 text-center font-mono font-bold text-slate-600">
                                                                {asig.asignatura_codigo || '-'}
                                                            </td>
                                                            <td className="px-3 py-2">
                                                                <span className="text-slate-800">{asig.asignatura_nombre}</span>
                                                            </td>
                                                            <td className="px-3 py-2 text-center">
                                                                <span className="px-1.5 py-0.5 rounded bg-slate-100 font-bold text-slate-600">
                                                                    Año {asig.nivel || 1}
                                                                </span>
                                                            </td>
                                                            <td className="px-3 py-2 text-center">
                                                                <span className="px-2 py-0.5 rounded-full font-bold bg-indigo-100 text-indigo-700">
                                                                    {asig.paralelo || 'A'}
                                                                </span>
                                                            </td>
                                                            <td className="px-3 py-2 text-slate-600">
                                                                {asig.docente_nombre || 'Por designar'}
                                                            </td>
                                                            <td className="px-3 py-2 text-center text-slate-500 text-[11px]">
                                                                {asig.dias ? (Array.isArray(asig.dias) ? asig.dias.join(', ') : asig.dias) : 'Por definir'}
                                                                {asig.hora_inicio && ` (${asig.hora_inicio.slice(0, 5)} - ${asig.hora_fin.slice(0, 5)})`}
                                                            </td>
                                                            <td className="px-3 py-2 text-center font-semibold text-slate-500">
                                                                {asig.sala || '-'}
                                                            </td>
                                                            <td className="px-3 py-2 text-center">
                                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                                                    cupoLleno ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                                                                }`} title={`Capacidad: ${asig.capacidad}, Inscritos: ${asig.inscritos}`}>
                                                                    {asig.inscritos}/{asig.capacidad}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Footer Modal con Acciones */}
                        <div className="p-4 bg-slate-50 border-t flex items-center justify-between">
                            <div className="text-xs text-slate-500">
                                * Al guardar, el estudiante pasará automáticamente a estado <strong>ACTIVO</strong> con sus materias registradas.
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowDesignarModal(false)}
                                    disabled={savingDesignacion}
                                    className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 transition"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveDesignacion}
                                    disabled={savingDesignacion || loadingOferta}
                                    className={`px-6 py-2 bg-blue-900 text-white rounded-lg text-xs font-bold flex items-center gap-2 shadow-md shadow-blue-900/20 hover:bg-blue-800 transition ${
                                        savingDesignacion ? 'opacity-70 cursor-not-allowed' : ''
                                    }`}
                                >
                                    {savingDesignacion ? (
                                        <>
                                            <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            <span>Guardando designación...</span>
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle2 size={16} />
                                            <span>Confirmar y Guardar ({selectedAsignaciones.length})</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KardexInscripciones;
